import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type ChangeEvent,
  type ClipboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Button, Textarea, Tooltip, TooltipContent, TooltipTrigger } from '@scio3/ui';
import { SendHorizonal, ImagePlus, Square } from 'lucide-react';
import { MAX_INPUT_LENGTH } from '../utils/constants';
import { isEnterKey } from '@/lib/keyboard';
import { ImagePreviewBar } from './ImagePreviewBar';
import type { UploadedImage } from '../hooks/useImageUpload';

interface OptiBotInputProps {
  input: string;
  setInput: (value: string) => void;
  onSend: (input: string) => void;
  isLoading: boolean;
  onStop: () => void;
  /** Image upload state */
  images: UploadedImage[];
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  acceptedExtensions: string;
  onOpenFilePicker: () => void;
  onFileSelect: (files: FileList | null) => void;
  onRemoveImage: (localId: string) => void;
}

/**
 * Chat input with auto-resize textarea, (+) image attach button, and send button.
 * Enter sends the message; Shift+Enter inserts a newline.
 */
export function OptiBotInput({
  input,
  setInput,
  onSend,
  isLoading,
  onStop,
  images,
  isUploading,
  fileInputRef,
  acceptedExtensions,
  onOpenFilePicker,
  onFileSelect,
  onRemoveImage,
}: OptiBotInputProps) {
  const { t } = useTranslation();

  // Show the focus ring only when focus arrives via keyboard (Tab), not on click.
  // Text fields always match :focus-visible (even on click), so CSS can't do this —
  // we track whether the focus was triggered by a pointer interaction.
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const focusFromPointer = useRef(false);

  const canSend = (input.trim().length > 0 || images.length > 0) && !isLoading && !isUploading;

  const handleSend = useCallback(() => {
    if (!canSend) return;
    onSend(input.trim());
    setInput('');
  }, [canSend, input, onSend, setInput]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (isEnterKey(e) && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value.length <= MAX_INPUT_LENGTH) {
        setInput(value);
      }
    },
    [setInput],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length === 0) return;

      e.preventDefault();
      const dt = new DataTransfer();
      imageFiles.forEach((f) => dt.items.add(f));
      onFileSelect(dt.files);
    },
    [onFileSelect],
  );

  return (
    <div className="shrink-0 px-3 pb-3">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedExtensions}
        multiple
        className="hidden"
        onChange={(e) => onFileSelect(e.target.files)}
      />
      {/* Single field container — textarea on top, action toolbar inside along the
          bottom (attach left, send right). shadow-sm lifts it off the messages, so no
          divider line above is needed. Keyboard-only focus ring lives on the container. */}
      <div
        className={cn(
          'rounded-xl border border-input bg-background shadow-sm transition-colors',
          'hover:border-muted-foreground/70',
          // Inset ring (drawn inside the field) so the seamless top edge has no
          // external offset to clip — keeps zero top padding above the input.
          keyboardFocus && 'ring-2 ring-inset ring-ring',
        )}
      >
        {images.length > 0 && (
          <div className="px-3 pt-3">
            <ImagePreviewBar images={images} onRemove={onRemoveImage} />
          </div>
        )}
        <Textarea
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onPointerDown={() => {
            focusFromPointer.current = true;
          }}
          onFocus={() => {
            setKeyboardFocus(!focusFromPointer.current);
            focusFromPointer.current = false;
          }}
          onBlur={() => setKeyboardFocus(false)}
          placeholder={t('optibot.placeholder')}
          maxLength={MAX_INPUT_LENGTH}
          rows={1}
          // Borderless inside the field container — the container owns the border/ring.
          // dark:bg-transparent overrides the base Textarea's dark:bg-card so the field
          // doesn't paint a lighter rectangle inside the container in dark mode.
          className={cn(
            'min-h-[44px] max-h-[120px] resize-none border-0 bg-transparent dark:bg-transparent px-3 pt-3 pb-1 shadow-none',
            'hover:border-transparent focus-visible:ring-0 focus-visible:ring-offset-0',
          )}
        />
        {/* Inline action toolbar */}
        <div className="flex items-center justify-between px-2 pb-2">
          {/* Left group: attach image. */}
          <div className="flex items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onOpenFilePicker}
                  disabled={isLoading || images.length >= 6}
                  aria-label={t('optibot.image.attach')}
                  className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                >
                  <ImagePlus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('optibot.image.attach')}</TooltipContent>
            </Tooltip>
          </div>
          {isLoading ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon-sm"
                  className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={onStop}
                  aria-label={t('optibot.stop')}
                >
                  <Square className="h-3.5 w-3.5 rounded-[1px] fill-current" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('optibot.stop')}</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-sm"
                  className="shrink-0 cursor-pointer"
                  onClick={handleSend}
                  disabled={!canSend}
                  aria-label={t('optibot.send')}
                >
                  <SendHorizonal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('optibot.send')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
